import Loader from "common/loader";
import { isValidElement } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSession } from "session";

type Props = {
  element: React.ReactNode | React.ComponentType;
  /** Permission the new backend must have granted, e.g. "order.read". */
  requires?: string;
};

/** Screens only an organization owner or admin should reach. */
const OWNER_PATHS = ["/staff", "/roleandgroups", "/approvals"];

/**
 * Guards a route using the session from the new backend.
 *
 * Previously this read a cookie plus several localStorage keys written by the
 * Xano login. Access is now decided by the session and its permissions.
 */
export const ProtectedRoute: React.FC<Props> = ({ element, requires }) => {
  const { pathname } = useLocation();
  const { session, loading, can } = useSession();

  // Never redirect while the stored token is still being checked.
  if (loading) return <Loader />;
  if (!session) return <Navigate to="/" replace />;

  const isOwner = session.role === "admin" || session.role === "partner_owner";
  if (!isOwner && OWNER_PATHS.includes(pathname)) {
    return <Navigate to="/dashboard" replace />;
  }
  if (requires && !can(requires)) {
    return <Navigate to="/404" replace />;
  }

  // A plain element renders as-is. Anything else is a component type, which
  // includes lazy() results: those are objects, so a typeof "function" check
  // silently rendered nothing for every lazily loaded page.
  if (isValidElement(element)) return element;
  const Component = element as React.ComponentType;
  return <Component />;
};
